import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AuthContext = createContext(null);

// API URL
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);


  const parseToken = useCallback(() => {
    const token = localStorage.getItem('token');
    if (!token) return null;
    
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));

      if (payload.exp * 1000 < Date.now()) {
        localStorage.removeItem('token');
        return null;
      }
      return payload;
    } catch (e) {
      localStorage.removeItem('token');
      return null;
    }
  }, []);

  const loadPermissions = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      setIsAuthenticated(false);
      return false;
    }

    try {
      const response = await fetch(`${API_URL}/users/me/permissions`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setUser({
          id: data.user_id,
          username: data.username,
          roles: data.roles
        });
        setPermissions(data.permissions);
        setIsAdmin(data.is_admin);
        setIsAuthenticated(true);
        return true;
      } else if (response.status === 401) {
        
        localStorage.removeItem('token');
        setIsAuthenticated(false);
        setUser(null);
        setPermissions([]);
        setIsAdmin(false);
        return false;
      }
    } catch (error) {
      console.error('İzinler yüklenirken hata:', error);
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

 
  useEffect(() => {
    loadPermissions();
  }, [loadPermissions]);

 
  const hasPermission = useCallback((permission) => {
    if (isAdmin) return true;
    if (permissions.includes('*')) return true;
    return permissions.includes(permission);
  }, [isAdmin, permissions]);

  
  const hasAnyPermission = useCallback((permissionList) => {
    if (isAdmin) return true;
    if (permissions.includes('*')) return true;
    return permissionList.some(perm => permissions.includes(perm));
  }, [isAdmin, permissions]);

  
  const hasAllPermissions = useCallback((permissionList) => {
    if (isAdmin) return true;
    if (permissions.includes('*')) return true;
    return permissionList.every(perm => permissions.includes(perm));
  }, [isAdmin, permissions]);

  
  const login = useCallback(async (username, password, rememberMe = false) => {
    const response = await fetch(`${API_URL}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password, remember_me: rememberMe })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Giriş başarısız');
    }

    const data = await response.json();
    localStorage.setItem('token', data.access_token);
    localStorage.setItem('lastActivity', Date.now().toString());
    
    if (rememberMe) {
      localStorage.setItem('rememberMe', 'true');
    } else {
      localStorage.removeItem('rememberMe');
    }

    
    await loadPermissions();

    return data;
  }, [loadPermissions]);

 
  const logout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('lastActivity');
    localStorage.removeItem('sessionExpiry');
    localStorage.removeItem('rememberMe');
    setUser(null);
    setPermissions([]);
    setIsAdmin(false);
    setIsAuthenticated(false);
  }, []);

 
  const refreshPermissions = useCallback(() => {
    return loadPermissions();
  }, [loadPermissions]);

  const value = {
    user,
    permissions,
    isAdmin,
    isAuthenticated,
    loading,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    login,
    logout,
    refreshPermissions
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}


export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}


export function RequirePermission({ permission, children, fallback = null }) {
  const { hasPermission, loading } = useAuth();
  
  if (loading) return null;
  if (!hasPermission(permission)) return fallback;
  return children;
}


export function RequireAdmin({ children, fallback = null }) {
  const { isAdmin, loading } = useAuth();
  
  if (loading) return null;
  if (!isAdmin) return fallback;
  return children;
}

export default AuthContext;